import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FaTimes } from 'react-icons/fa';
import PropTypes from 'prop-types';

/**
 * A standardized Modal component that enforces the iShopMaster brand aesthetic.
 * 
 * @param {boolean} show - Control visibility
 * @param {function} onHide - Close handler
 * @param {string} title - Main title text
 * @param {string} subtitle - Optional subtitle text below title
 * @param {React.ReactNode} icon - Optional icon component (e.g. <FaPlus />)
 * @param {string} size - Bootstrap modal size (sm, lg, xl) or 'fullscreen' boolean prop in standard Modal
 * @param {boolean} centered - Vertically center
 * @param {React.ReactNode} children - Modal body content
 * @param {React.ReactNode} footer - Optional footer content
 * @param {string} className - Extra classes
 */
const BrandModal = ({
    show,
    onHide,
    title,
    subtitle,
    icon,
    size = 'lg',
    centered = true,
    children,
    footer,
    fullscreen,
    className = "",
    ...props
}) => {
    return (
        <Modal
            show={show}
            onHide={onHide}
            fullscreen={size === 'fullscreen' || fullscreen}
            size={size === 'fullscreen' || fullscreen ? undefined : size}
            centered={centered}
            backdrop="static"
            className={`fade-in-up ${className}`}
            {...props}
        >
            <Modal.Header className="border-bottom bg-white py-2.5 px-3 px-md-4 sticky-top shadow-sm" style={{ zIndex: 1050 }}>
                <div className="w-100 d-flex align-items-center justify-content-between gap-2">
                    <div className="d-flex align-items-center gap-2 gap-md-3 min-w-0 flex-grow-1">
                        {icon && (
                            <div
                                className="p-2 bg-primary bg-opacity-10 rounded-circle text-primary shadow-xs d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{ width: '36px', height: '36px' }}
                            >
                                {icon}
                            </div>
                        )}
                        <div className="min-w-0 flex-grow-1">
                            <h5
                                className="mb-0 fw-bold text-dark text-break"
                                style={{ fontSize: 'clamp(0.85rem, 2.5vw, 1.05rem)', lineHeight: '1.25' }}
                                title={typeof title === 'string' ? title : undefined}
                            >
                                {title}
                            </h5>
                            {subtitle && (
                                <div className="text-muted small fw-medium mt-0.5" style={{ fontSize: '0.72rem' }}>
                                    {subtitle}
                                </div>
                            )}
                        </div>
                    </div>

                    <Button
                        variant="light"
                        size="sm"
                        onClick={onHide}
                        className="d-flex align-items-center justify-content-center border rounded-circle flex-shrink-0 shadow-2xs"
                        style={{ width: "34px", height: "34px", minWidth: "34px" }}
                        title="Close"
                    >
                        <FaTimes size={14} />
                    </Button>
                </div>
            </Modal.Header>
            <Modal.Body className="bg-light bg-opacity-50 p-0" style={{ display: 'flex', flexDirection: 'column', overflowY: 'auto', flex: 1, minHeight: 0 }}>
                {children}
            </Modal.Body>
            {footer && (
                <Modal.Footer className="bg-white border-top px-4 py-3">
                    {footer}
                </Modal.Footer>
            )}
        </Modal>
    );
};

BrandModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    icon: PropTypes.node,
    size: PropTypes.string,
    centered: PropTypes.bool,
    children: PropTypes.node,
    footer: PropTypes.node,
    className: PropTypes.string,
};

export default BrandModal;
